import React, { useState, useEffect } from 'react';
import {
  Loader2, X, Upload, FileText, Send, PenLine,
  ChevronRight, ChevronLeft, Check, User, Shield, ClipboardList
} from 'lucide-react';
import { api } from '../../api';

const STEPS = [
  { id: 1, label: 'Client Info', icon: User },
  { id: 2, label: 'Intake Form', icon: ClipboardList },
  { id: 3, label: 'Policy Docs', icon: Shield },
];

export default function AddClientModal({ onClose, onClientAdded }) {
  const [step, setStep] = useState(1);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1 — Basic Info
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    producer_id: '',
    account_executive_id: '',
  });

  // Step 2 — Intake
  const [intakeMethod, setIntakeMethod] = useState(null); // 'send' | 'skip'

  // Step 3 — Policy Docs
  const [docMethod, setDocMethod] = useState(null); // 'upload' | 'analyze' | 'manual' | 'skip'
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [policyInfo, setPolicyInfo] = useState({
    policyNumber: '',
    carrier: '',
    expirationDate: '',
    coverageType: '',
    premium: '',
    address: '',
    riskState: '',
    effectiveDate: '',
  });

  useEffect(() => {
    api('/api/users').then(setUsers).catch(console.error);
  }, []);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleFileUpload = (e) => setUploadedFiles(prev => [...prev, ...Array.from(e.target.files)]);
  const removeFile = (index) => setUploadedFiles(prev => prev.filter((_, i) => i !== index));

  const getToken = () => localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken') || '';

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Create the client
      const payload = {
        ...form,
        producer_id: form.producer_id || null,
        account_executive_id: form.account_executive_id || null,
        ...(docMethod === 'manual' ? policyInfo : {}),
      };
      const newClient = await api('/api/clients', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      // 2. Send intake form using raw fetch (avoids api() throwing on non-JSON or error responses)
      if (intakeMethod === 'send' && form.email) {
        await fetch(`/api/clients/${newClient.id}/intake/resend`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`,
          },
          body: JSON.stringify({ email: form.email }),
        });
      }

      // 3. Upload files if any (both 'upload' and 'analyze' save files)
      if ((docMethod === 'upload' || docMethod === 'analyze') && uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          const fd = new FormData();
          fd.append('file', file);
          await fetch(`/api/clients/${newClient.id}/files`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${getToken()}` },
            body: fd,
          });
        }
      }

      // 4. If analyze mode, run AI extraction and patch the client with extracted data
      if (docMethod === 'analyze' && uploadedFiles.length > 0) {
        try {
          const fd = new FormData();
          uploadedFiles.forEach(f => fd.append('files', f));
          const analyzeRes = await fetch(`/api/clients/${newClient.id}/analyze-docs`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${getToken()}` },
            body: fd,
          });
          if (analyzeRes.ok) {
            const extracted = await analyzeRes.json();
            // Patch client with any extracted policy info
            if (extracted.policies && extracted.policies[0]) {
              const p = extracted.policies[0];
              await fetch(`/api/clients/${newClient.id}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${getToken()}`,
                },
                body: JSON.stringify({
                  policyNumber: p.policyNumber || null,
                  carrier: p.carrier || null,
                  coverageType: p.coverageType || null,
                  effectiveDate: p.effectiveDate || null,
                  expirationDate: p.expirationDate || null,
                  premium: p.premium || null,
                }),
              });
            }
          }
        } catch (e) {
          console.warn('AI analysis failed, client still created:', e);
        }
      }

      onClientAdded(newClient);
    } catch (err) {
      setError(err.message || 'Failed to add client');
      setLoading(false);
    }
  };

  const states = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

  const canProceedStep1 = form.firstName.trim() && form.lastName.trim() && form.email.trim();
  const canProceedStep2 = intakeMethod !== null;
  const canProceedStep3 = docMethod !== null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">Add New Client</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition">
            <X size={20} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 pt-5 pb-2">
          <div className="flex items-center justify-between relative">
            <div className="absolute top-4 h-px bg-slate-200 z-0" style={{ left: '2.5rem', right: '2.5rem' }} />
            {STEPS.map((s) => {
              const Icon = s.icon;
              const done = step > s.id;
              const active = step === s.id;
              return (
                <div key={s.id} className="flex flex-col items-center z-10" style={{ flex: 1 }}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                    done ? 'bg-blue-600 border-blue-600 text-white' :
                    active ? 'bg-white border-blue-600 text-blue-600' :
                    'bg-white border-slate-300 text-slate-400'
                  }`}>
                    {done ? <Check size={14} strokeWidth={3} /> : <Icon size={14} />}
                  </div>
                  <span className={`mt-1.5 text-xs font-medium ${active ? 'text-blue-600' : done ? 'text-slate-600' : 'text-slate-400'}`}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4">

          {/* ── STEP 1: Basic Client Info ── */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Enter basic info. The intake form will collect the rest directly from the client.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input name="lastName" value={form.lastName} onChange={handleChange} required
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input name="firstName" value={form.firstName} onChange={handleChange} required
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input name="email" type="email" value={form.email} onChange={handleChange}
                  placeholder="client@example.com"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input name="phone" type="tel" value={form.phone} onChange={handleChange}
                  placeholder="(555) 000-0000"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Producer</label>
                <select name="producer_id" value={form.producer_id} onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
                  <option value="">Select producer...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Account Executive</label>
                <select name="account_executive_id" value={form.account_executive_id} onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
                  <option value="">Select account executive...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* ── STEP 2: Intake Form ── */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Send <span className="font-semibold text-slate-700">{form.firstName} {form.lastName}</span> a secure intake form to collect their personal, residence, vehicle, and liability details.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setIntakeMethod('send')}
                  className={`flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all text-left ${
                    intakeMethod === 'send' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${intakeMethod === 'send' ? 'bg-blue-100' : 'bg-slate-100'}`}>
                    <Send size={20} className={intakeMethod === 'send' ? 'text-blue-600' : 'text-slate-500'} />
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${intakeMethod === 'send' ? 'text-blue-700' : 'text-slate-700'}`}>Send Intake Form</div>
                    <div className="text-xs text-slate-500 mt-0.5">Emailed to {form.email} immediately</div>
                  </div>
                </button>

                <button onClick={() => setIntakeMethod('skip')}
                  className={`flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all text-left ${
                    intakeMethod === 'skip' ? 'border-slate-400 bg-slate-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100">
                    <PenLine size={20} className="text-slate-500" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-700">Skip for Now</div>
                    <div className="text-xs text-slate-500 mt-0.5">Send from the client page later</div>
                  </div>
                </button>
              </div>

              {intakeMethod === 'send' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 space-y-1">
                  <p className="text-sm font-medium text-blue-800">Intake form will be sent to:</p>
                  <p className="text-sm font-bold text-blue-700">{form.email}</p>
                  <p className="text-xs text-blue-600 mt-1">The client receives a secure link covering personal info, home details, vehicles, and liability. You'll be notified when complete.</p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: Policy Docs ── */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Do you have existing policy documents for this client?</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'analyze', icon: 'sparkle', label: 'Upload & Analyze', desc: 'AI extracts policy info automatically' },
                  { key: 'upload', icon: Upload, label: 'Upload to Files', desc: 'Save docs without analysis' },
                  { key: 'manual', icon: PenLine, label: 'Enter Manually', desc: 'Type in policy details' },
                  { key: 'skip', icon: X, label: 'Skip for Now', desc: 'Add documents later' },
                ].map(({ key, icon: Icon, label, desc }) => (
                  <button key={key} onClick={() => { setDocMethod(key); setAnalyzed(false); setAnalyzeError(null); }}
                    className={`flex flex-col gap-2 p-4 rounded-2xl border-2 transition-all text-left ${
                      docMethod === key
                        ? key === 'skip' ? 'border-slate-400 bg-slate-50' : 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                      docMethod === key && key !== 'skip' ? 'bg-blue-100' : 'bg-slate-100'
                    }`}>
                      {key === 'analyze'
                        ? <span className={`text-lg ${docMethod === key ? 'text-blue-600' : 'text-slate-500'}`}>✦</span>
                        : <Icon size={18} className={docMethod === key && key !== 'skip' ? 'text-blue-600' : 'text-slate-500'} />
                      }
                    </div>
                    <div>
                      <div className={`text-xs font-semibold ${docMethod === key && key !== 'skip' ? 'text-blue-700' : 'text-slate-700'}`}>{label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{desc}</div>
                    </div>
                  </button>
                ))}
              </div>

              {(docMethod === 'upload' || docMethod === 'analyze') && (
                <div className="space-y-3">
                  <label className={`flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed rounded-xl py-6 cursor-pointer transition ${
                    uploadedFiles.length > 0 ? 'border-blue-300 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
                  }`}>
                    <Upload size={22} className="text-slate-400" />
                    <span className="text-sm text-slate-500">Click to upload policy documents</span>
                    <span className="text-xs text-slate-400">PDF, PNG, JPG accepted</span>
                    <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={handleFileUpload} />
                  </label>
                  {uploadedFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2 border border-slate-200">
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <FileText size={15} className="text-blue-500" />
                        <span className="truncate max-w-[260px]">{f.name}</span>
                      </div>
                      <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500 transition">
                        <X size={14} />
                      </button>
                    </div>
                  ))}

                  {docMethod === 'analyze' && uploadedFiles.length > 0 && !analyzed && (
                    <button onClick={async () => {
                      setAnalyzing(true); setAnalyzeError(null);
                      try {
                        const fd = new FormData();
                        uploadedFiles.forEach(f => fd.append('files', f));
                        // We'll use a temp token-based analyze — clientId not known yet,
                        // so store extracted data in policyInfo for manual review
                        setAnalyzed(true);
                        setPolicyInfo(p => ({ ...p, _pendingAnalysis: true }));
                      } catch { setAnalyzeError('Analysis failed. Files will still be saved.'); }
                      finally { setAnalyzing(false); }
                    }} disabled={analyzing}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition disabled:opacity-60">
                      {analyzing ? <><Loader2 size={14} className="animate-spin" /> Analyzing...</> : <>✦ Extract with AI</>}
                    </button>
                  )}

                  {analyzed && docMethod === 'analyze' && (
                    <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 text-xs text-green-700 font-medium">
                      ✓ Files will be saved and analyzed after client is created
                    </div>
                  )}

                  {analyzeError && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-700">{analyzeError}</div>
                  )}
                </div>
              )}

              {docMethod === 'manual' && (
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'policyNumber', label: 'Policy Number', placeholder: 'e.g. POL-123456' },
                    { key: 'carrier', label: 'Carrier', placeholder: 'e.g. State Farm' },
                    { key: 'effectiveDate', label: 'Effective Date', type: 'date' },
                    { key: 'expirationDate', label: 'Expiration Date', type: 'date' },
                    { key: 'premium', label: 'Annual Premium', placeholder: 'e.g. 2400' },
                  ].map(({ key, label, placeholder, type = 'text' }) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                      <input type={type} value={policyInfo[key]} placeholder={placeholder}
                        onChange={e => setPolicyInfo(p => ({ ...p, [key]: e.target.value }))}
                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Coverage Type</label>
                    <select value={policyInfo.coverageType} onChange={e => setPolicyInfo(p => ({ ...p, coverageType: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
                      <option value="">Select...</option>
                      {['Home','Auto','Umbrella','Commercial General Liability',"Business Owner's Policy","Workers' Compensation",'Professional Liability','Other'].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Risk State</label>
                    <select value={policyInfo.riskState} onChange={e => setPolicyInfo(p => ({ ...p, riskState: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
                      <option value="">Select...</option>
                      {states.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Address</label>
                    <input value={policyInfo.address} placeholder="123 Main St, City, State"
                      onChange={e => setPolicyInfo(p => ({ ...p, address: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-between items-center bg-slate-50 rounded-b-2xl">
          <button
            onClick={() => step === 1 ? onClose() : setStep(s => s - 1)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-200 transition"
          >
            <ChevronLeft size={15} />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          <div className="flex items-center gap-1.5">
            {STEPS.map(s => (
              <div key={s.id} className={`rounded-full transition-all ${step === s.id ? 'w-4 h-2 bg-blue-600' : 'w-2 h-2 bg-slate-300'}`} />
            ))}
          </div>

          {step < 3 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight size={15} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canProceedStep3 || loading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading && <Loader2 className="animate-spin" size={15} />}
              {loading ? 'Submitting...' : 'Add Client'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}